<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="deleteDieCutterDialog"
            :data="currentDieCutter"
            removal
            @on-confirm="deleteDieCutter"
            @on-abort="closeDeleteDieCutterDialog"
        >
            <template #title>
                Usuwanie wykrojnika
            </template>
            <template #content>
                Czy na pewno chcesz usunąć wykrojnik?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <page-title>Lista wykrojników</page-title>
        <v-row align="center" justify="space-between">
            <v-col cols="12" sm="4" lg="6">
                <v-btn
                    v-if="canCreateDieCutter"
                    color="secondary"
                    medium
                    class="ma-2"
                    data-sentry-label="Dodaj wykrojnik DMT"
                    @click="createDieCutter('DMT')"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Nowy wykrojnik DMT
                </v-btn>
                <v-btn
                    v-if="canCreateDieCutter"
                    color="secondary"
                    medium
                    class="ma-2"
                    data-sentry-label="Dodaj wykrojnik CF"
                    @click="createDieCutter('CF')"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Nowy wykrojnik CF
                </v-btn>
            </v-col>
            <v-col cols="12" sm="4" lg="6">
                <v-container fluid class="pa-0">
                    <v-row justify="end">
                        <v-col cols="12" md="8" lg="6">
                            <v-text-field
                                v-model="filters.symbol"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('symbol')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="dieCutters"
                        :loading="dieCuttersLoading"
                        :footer-props="footerProps"
                        :page.sync="pagination.page"
                        :server-items-length="pagination.itemsLength"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="DieCutterData"
                        @pagination="updatePagination"
                    >
                        <template #item.symbol="{item}">
                            {{ item.symbol }}
                        </template>
                        <template #item.productType="{item}">
                            {{ formattedProductTypeName(item) }}
                        </template>
                        <template #item.format="{item}">
                            {{ formattedFormat(item) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="openDieCutter(item.id)"
                                        >
                                            <v-icon color="primary">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            v-if="canDeleteDieCutter"
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="showDeleteDieCutterDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import customFilters from '@/mixins/customFilters'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { hasUserPermission } from '@/utils/permissionsHandler'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'DieCuttersList',
    components: {
        PageTitle,
        ConfirmDialog,
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dateFormatter,
        customFilters,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('configurations/tools/clearDieCuttersFilters')
            this.clearSelectedListItemId()
        }
        next()
    },
    data() {
        return {
            dieCutterDialog: false,
            deleteDieCutterDialog: false,
            currentDieCutter: null,
            headers: [
                {
                    text: 'Symbol wykrojnika',
                    value: 'symbol',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Produkt',
                    value: 'productType',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Format',
                    value: 'format',
                    sortable: false,
                    width: 200,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        dieCutters() {
            return this.$store.state.configurations.tools.dieCutters.dieCutters
        },
        dieCuttersLoading() {
            return this.$store.state.configurations.tools.dieCutters.dieCuttersLoading
        },
        canCreateDieCutter() {
            return hasUserPermission('die_cutters_admin_create')
        },
        canDeleteDieCutter() {
            return hasUserPermission('die_cutters_admin_delete')
        },
        selectedDieCutterId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getDieCutters()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedDieCutterId) {
                return 'isSelected'
            }
        },
        getDieCutters() {
            this.$store.dispatch('configurations/tools/getDieCutters', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createDieCutter(type) {
            const type_id = type === 'DMT' ? 1 : 2

            this.$router.push({
                name: 'ConfigurationsToolsDieCuttersDieCutterCreate',
                params: { typeId: type_id }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openDieCutter(dieCutterId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(dieCutterId)
            this.$router.push({
                name: 'ConfigurationsToolsDieCuttersDieCutter',
                params: { id: dieCutterId }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showDeleteDieCutterDialog(dieCutter) {
            this.deleteDieCutterDialog = true
            this.currentDieCutter = dieCutter
        },
        closeDeleteDieCutterDialog() {
            this.deleteDieCutterDialog = false
            this.currentDieCutter = null
        },
        deleteDieCutter(currentDieCutter) {
            this.dieCutterLoadingOnDelete = true
            this.$store.dispatch('configurations/tools/deleteDieCutter', currentDieCutter.id)
                .then(() => {
                    this.getDieCutters()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteDieCutterDialog()
                    this.dieCutterLoadingOnDelete = false
                })
        },
        changeFilter() {
            this.$store.dispatch('configurations/tools/setDieCuttersFilters', this.filters)
            this.resetPagination()
            this.getDieCutters()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getDieCutters()
            }
            this.clearPaginationPending()
        },
        formattedProductTypeName(dieCutter) {
            return dieCutter.productType ? dieCutter.productType.name : '-' 
        },
        formattedFormat(dieCutter) {
            return dieCutter.format ? dieCutter.format : '-'
        }
    }
}
</script>
